import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { getCampaigns } from "@/features/campaigns/api/campaignsApi";
import { moveContactsBatch, preflightEnrollment } from "@/features/campaigns/api/enrollmentsApi";
import { useAddLeadsToCampaign } from "@/features/campaigns/hooks/useCampaign";
import { useGenerateMatchScore } from "@/features/contacts/hooks/useContacts";
import {
  applyEmailValidationResults,
  buildScoredContacts,
} from "@/features/contacts/utils/matchScoreReview";
import { startEnrollmentBatchFromResponse } from "@/features/campaigns/utils/enrollmentBatchToast";
import { showError, showInfo, showSuccess, showWarning } from "@/shared/utils/toast";
import { trialLeadSkipMessage } from "@/shared/utils/trialLimits";

const MOVE_TYPE_TO_MODE = {
  move_to_campaigns: "campaigns",
  move_to_accounts: "accounts",
  move_to_both: "both",
};

const UI_MODE_TO_API_MODE = {
  campaigns: "campaign",
  accounts: "account",
  both: "both",
};

function getContactIds(contacts) {
  return contacts
    .map((contact) => contact?.id ?? contact?.contact_id ?? contact?.contactId)
    .filter(Boolean)
    .map(String);
}

const companyCount = (n) => `${n} ${n === 1 ? "company" : "companies"}`;
const contactCount = (n) => `${n} contact${n === 1 ? "" : "s"}`;

function summarizeSkipReasons(skipped = []) {
  return skipped.reduce((counts, item) => {
    const reason = String(item?.reason || "UNKNOWN");
    counts[reason] = (counts[reason] || 0) + 1;
    return counts;
  }, {});
}

function getAllSkippedMessage(skipped = []) {
  const trialMessage = trialLeadSkipMessage(skipped);
  if (trialMessage) return trialMessage;
  const counts = summarizeSkipReasons(skipped);
  const entries = Object.entries(counts);
  if (entries.length === 1) {
    const [reason, count] = entries[0];
    const noun = `${count} contact${count !== 1 ? "s" : ""}`;
    if (reason === "ALREADY_ENROLLED") return `${noun} already in this campaign.`;
    if (reason === "CONTACT_ENROLLED_IN_OTHER_CAMPAIGN") {
      return `${noun} already enrolled in another campaign.`;
    }
    if (reason.startsWith("EMAIL_CONSENT_")) {
      return `${noun} skipped because email consent is not available.`;
    }
    if (reason.startsWith("EMAIL_")) {
      return `${noun} skipped because the email ${count !== 1 ? "addresses are" : "address is"} not deliverable.`;
    }
  }
  return `${skipped.length} contact${skipped.length !== 1 ? "s were" : " was"} skipped and could not be added to this campaign.`;
}

function emitEnrollmentBatchStarted(data, { campaignId, mode }) {
  return startEnrollmentBatchFromResponse(data, { campaignId, mode });
}

const useMoveContacts = ({ selectedContacts, userId, onComplete }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [moveMode, setMoveMode] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [reviewContacts, setReviewContacts] = useState([]);
  const [scoreResponse, setScoreResponse] = useState(null);
  const [isResolvingCampaign, setIsResolvingCampaign] = useState(false);
  const [isValidatingMoveEmails, setIsValidatingMoveEmails] = useState(false);
  const [isExecutingMove, setIsExecutingMove] = useState(false);

  const {
    mutateAsync: addLeadsToCampaignAsync,
    isPending: isAddingToCampaign,
    reset: resetAddMutation,
  } = useAddLeadsToCampaign();
  const {
    mutateAsync: generateMatchScoreAsync,
    isPending: isGeneratingMoveScores,
  } = useGenerateMatchScore();

  const contactIds = useMemo(
    () => getContactIds(selectedContacts),
    [selectedContacts],
  );

  const closeCampaignModal = useCallback(() => {
    setShowCampaigns(false);
    setShowReview(false);
    setMoveMode(null);
    setSelectedCampaign(null);
    setReviewContacts([]);
    setScoreResponse(null);
    resetAddMutation();
    onComplete?.();
  }, [onComplete, resetAddMutation]);

  const handleCampaignSelected = useCallback(
    async (campaign, responseOverride = scoreResponse) => {
      console.info("[MoveCampaign][campaign-selected]", {
        campaignId: campaign?.id,
        hasScoreResponse: Boolean(responseOverride),
        selectedContactsCount: selectedContacts.length,
      });
      if (!campaign?.id || !responseOverride) {
        console.info("[MoveCampaign][campaign-selection-blocked]");
        return;
      }

      setIsValidatingMoveEmails(true);
      try {
        const validationResponse = await preflightEnrollment({
          campaignId: campaign.id,
          contactIds: getContactIds(selectedContacts),
        });
        const scoredContacts = buildScoredContacts(
          selectedContacts,
          responseOverride,
          campaign.id,
        );
        setSelectedCampaign(campaign);
        setReviewContacts(applyEmailValidationResults(scoredContacts, validationResponse));
        setShowCampaigns(false);
        setShowReview(true);
      } catch (error) {
        showError(error?.message || "Failed to validate contact email addresses.");
      } finally {
        setIsValidatingMoveEmails(false);
      }
    },
    [scoreResponse, selectedContacts],
  );

  const executeCampaignMove = useCallback(
    async (campaign, modeOverride, contactsOverride = reviewContacts) => {
      const campaignId = String(campaign?.id ?? campaign ?? selectedCampaign?.id);
      const activeMode = modeOverride ?? moveMode;
      const apiMode = UI_MODE_TO_API_MODE[activeMode];
      const contactsToEnroll = Array.isArray(contactsOverride)
        ? contactsOverride
        : [];
      if (
        !campaignId ||
        !apiMode ||
        !["campaign", "both"].includes(apiMode) ||
        contactsToEnroll.length === 0
      ) {
        return;
      }

      const formattedContacts = contactsToEnroll
        .map((contact) => contact?.id ?? contact?.contact_id ?? contact?.contactId)
        .filter(Boolean)
        .map((contactId) => ({ contactId }));
      if (formattedContacts.length === 0) return;

      setIsExecutingMove(true);
      try {
        const enrollmentResult = await addLeadsToCampaignAsync({
          campaignId,
          contacts: formattedContacts,
          mode: apiMode,
          suppressEnrollmentToasts: true,
        });
        const enrolledCount = Number(enrollmentResult?.count ?? 0);
        const skippedCount = Array.isArray(enrollmentResult?.skipped)
          ? enrollmentResult.skipped.length
          : 0;
        const emailSkippedCount = Array.isArray(enrollmentResult?.skipped)
          ? enrollmentResult.skipped.filter((item) =>
              String(item?.reason || "").startsWith("EMAIL_") &&
                !String(item?.reason || "").startsWith("EMAIL_CONSENT_"),
            ).length
          : 0;
        const allSkipped = enrolledCount === 0 && skippedCount > 0;

        console.info("[MoveCampaign][move-result]", {
          campaignId,
          requestedCount: formattedContacts.length,
          enrolledCount,
          skippedCount,
          skippedByReason: summarizeSkipReasons(enrollmentResult?.skipped),
        });

        setShowReview(false);
        setShowCampaigns(false);
        setMoveMode(null);
        setSelectedCampaign(null);
        setReviewContacts([]);
        setScoreResponse(null);
        resetAddMutation();
        onComplete?.();

        if (allSkipped) {
          showWarning(getAllSkippedMessage(enrollmentResult?.skipped));
          return;
        }

        if (skippedCount > 0) {
          showWarning(
            `${enrolledCount} contact${enrolledCount !== 1 ? "s" : ""} added to campaign. ${skippedCount} skipped${emailSkippedCount > 0 ? `, including ${emailSkippedCount} with undeliverable email${emailSkippedCount !== 1 ? "s" : ""}` : "."}`,
          );
        } else {
          showSuccess(
            `${enrolledCount} contact${enrolledCount !== 1 ? "s" : ""} added to campaign successfully.`,
          );
        }

        navigate(`/campaigns/${campaignId}/leads`);
      } catch (error) {
        console.error("[MoveCampaign][move-failed]", {
          campaignId,
          requestedCount: formattedContacts.length,
          message: error?.message || String(error),
        });
        showError(
          error?.message ||
            "Failed to move contacts to campaign. Please try again.",
        );
      } finally {
        setIsExecutingMove(false);
      }
    },
    [
      addLeadsToCampaignAsync,
      moveMode,
      navigate,
      onComplete,
      resetAddMutation,
      reviewContacts,
      selectedCampaign,
    ],
  );

  const handleMoveTo = useCallback(
    async (moveType) => {
      const nextMode = MOVE_TYPE_TO_MODE[moveType];
      if (!nextMode || contactIds.length === 0) return;

      if (nextMode === "accounts") {
        setIsResolvingCampaign(true);
        try {
          const formattedContacts = contactIds.map((contactId) => ({
            contactId,
          }));
          const result = await moveContactsBatch({
            mode: "account",
            contacts: formattedContacts,
          });

          const toastStarted = emitEnrollmentBatchStarted(result, {
            mode: "account",
          });
          queryClient.invalidateQueries({ queryKey: ["accounts"] });

          // When a batch starts, the progress toast shows the contact/company counts itself.
          const summary = result?.accountSummary;
          if (summary && summary.queued === 0) {
            const companies = summary.alreadyInAccounts;
            showInfo(
              companies === 1
                ? "This company is already in Accounts."
                : `All ${companyCount(companies)} are already in Accounts.`,
              `The ${contactCount(summary.selected)} you selected belong to existing accounts, so nothing new was added.`,
            );
          } else if (!toastStarted) {
            showSuccess(
              `${contactIds.length} contact${contactIds.length !== 1 ? "s" : ""} moved to accounts successfully.`,
            );
          }
          onComplete?.();
        } catch (error) {
          showError(
            error?.message ||
              "Failed to move contacts to accounts. Please try again.",
          );
        } finally {
          setIsResolvingCampaign(false);
        }
        return;
      }

      setMoveMode(nextMode);
      setShowCampaigns(true);
      setIsResolvingCampaign(true);
      try {
        if (!userId) {
          throw new Error("Unable to generate match scores without a signed-in user.");
        }

        const nextScoreResponse = await generateMatchScoreAsync({
          userId,
          contactIds,
        });
        const scoredContacts = buildScoredContacts(
          selectedContacts,
          nextScoreResponse,
        );
        const unscoredCount = scoredContacts.filter(
          (contact) => contact.scoreError,
        ).length;
        if (unscoredCount > 0) {
          throw new Error(
            `Match scores could not be generated for ${unscoredCount} selected contact${unscoredCount !== 1 ? "s" : ""}. No contacts were enrolled.`,
          );
        }

        setScoreResponse(nextScoreResponse);
        const campaignsData = await getCampaigns(1, 1, "");
        const totalCount = campaignsData?.totalCount ?? 0;
        const onlyCampaign = campaignsData?.campaigns?.[0];

        if (totalCount === 1 && onlyCampaign?.id) {
          await handleCampaignSelected(onlyCampaign, nextScoreResponse);
        }
      } catch (error) {
        setShowCampaigns(false);
        setMoveMode(null);
        setScoreResponse(null);
        showError(
          error?.message || "Failed to load campaigns. Please try again.",
        );
      } finally {
        setIsResolvingCampaign(false);
      }
    },
    [
      contactIds,
      generateMatchScoreAsync,
      handleCampaignSelected,
      onComplete,
      queryClient,
      selectedContacts,
      userId,
    ],
  );

  const handleReviewBack = useCallback(() => {
    console.info("[MoveCampaign][review-back]");
    setShowReview(false);
    setSelectedCampaign(null);
    setShowCampaigns(true);
  }, []);

  return {
    handleMoveTo,
    handleCampaignSelected,
    executeCampaignMove,
    showCampaigns,
    showReview,
    reviewContacts,
    selectedCampaign,
    moveMode,
    isGeneratingMoveScores,
    isValidatingMoveEmails,
    isMoving:
      isResolvingCampaign ||
      isGeneratingMoveScores ||
      isValidatingMoveEmails ||
      isAddingToCampaign ||
      isExecutingMove,
    handleReviewBack,
    closeCampaignModal,
  };
};

export default useMoveContacts;
